import React, {type ChangeEventHandler, useCallback, useEffect, useState} from 'react';

import {CopyButton} from 'src/components/copy-button';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {getAccountTemplateUsers} from 'src/openapi/admin/apis/GetAccountTemplateUsersApi';
import {useAppDispatch} from 'src/store/hooks';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {Autosuggest} from '@pb/kit-component-autosuggest-v2';
import {CheckBox} from '@pb/kit-component-checkbox';
import {Sizes} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';


import {TemplateUserList} from './template-user-list';

import type {UserShortInfo} from 'src/openapi/admin/models/UserShortInfo';
import type {ServerResponse} from 'src/typedef';

type Props = {
    width: number | string,
    selectedTemplateUser?: UserShortInfo,
    label: React.ReactNode,
    accountId?: number,
    onSelect: (user: UserShortInfo | undefined) => void,
    placeholder: string,
    disabled?: boolean,
    color?: 'DEFAULT' | 'SUCCESS' | 'ERROR' | undefined,
};

const MAX_RESULTS = 30;

export const SearchTemplateUser: React.FC<Props> = props => {
    const {selectedTemplateUser, label, onSelect, accountId, disabled, width, placeholder, color} = props;
    const apiDispatch = useAppDispatch();

    const [query, setQuery] = useState(
        selectedTemplateUser ? selectedTemplateUser.email : '');

    const [busy, setBusy] = useState(false);
    const [onlyActive, setOnlyActive] = useState(false);
    const [currentUser, setCurrentUser] = useState<UserShortInfo | undefined>(selectedTemplateUser);
    const [shown, setShown] = useState(false);

    const [allUsersResponse, setAllUsersResponse] = useState<ServerResponse<Array<UserShortInfo>> | null>(null);
    const [response, setResponse] = useState<ServerResponse<Array<UserShortInfo>> | null>(null);

    const loadTemplateUsers = useCallback(async () => {
        setBusy(true);
        const templateUserResponse = await apiDispatch(
            getAccountTemplateUsers(accountId ? {accountId, onlyActive} : {onlyActive}),
        );

        if (templateUserResponse.payload.success) {
            setAllUsersResponse({data: templateUserResponse.payload.data});
        } else {
            setAllUsersResponse({error: {message: 'Can\'t suggest template users. Service is temporary unavailable.'}});
        }
        setBusy(false);
    }, [onlyActive, accountId, apiDispatch]);

    useEffect(() => {
        loadTemplateUsers();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffectWithDeps((prev, current) => {
        if (!isDeepEqual({prev, current})) {
            setQuery(current ? current.email : '');
        }
    }, selectedTemplateUser);

    const getSuggestions = useCallback((newValue: string) => {
        const preparedQuery = newValue.toLowerCase().trim();
        if (allUsersResponse?.data) {
            const userShortInfos = allUsersResponse.data.filter(({firstName, lastName, email}) =>
                firstName.toLowerCase().startsWith(preparedQuery) ||
                lastName.toLowerCase().startsWith(preparedQuery) ||
                [firstName, lastName].join(' ').toLowerCase().trim().startsWith(preparedQuery) ||
                email.toLowerCase().startsWith(preparedQuery),
            ).slice(0, MAX_RESULTS);

            setResponse({data: userShortInfos});
        } else {
            setResponse({error: {message: 'Can\'t template users. Service is temporary unavailable.'}});
        }
    }, [allUsersResponse]);

    const onChangeQuery = useCallback<ChangeEventHandler<HTMLInputElement>>(event => {
        const value = event.currentTarget.value;
        setQuery(value);
        getSuggestions(value);
    }, [getSuggestions]);

    const clearSuggest = useCallback(() => {
        setQuery('');
        setCurrentUser(undefined);
        onSelect(undefined);
        setResponse(null);
    }, [onSelect]);

    const onItemSelect = useCallback((user: UserShortInfo) => {
        onSelect(user);
        setCurrentUser(user);
        setQuery(user.email);
        setResponse(null);
    }, [onSelect]);

    const onToggleActive = useCallback(() => {
        const value = !onlyActive;
        clearSuggest();
        setOnlyActive(value);
        loadTemplateUsers();
    }, [onlyActive, loadTemplateUsers, clearSuggest]);

    const onClick = useCallback(() => {
        setShown(true);
        getSuggestions(query);
    }, [getSuggestions, query]);

    return (
        <div style={{width}}>
            <Label htmlFor="template-user-suggest">
                <div style={{display: 'flex', alignItems: 'center'}}>
                    {label}
                    {currentUser ? (
                        <CopyButton
                            copiedText={currentUser.email}
                            entityName="Template user email"
                            iconSize={Sizes.Size_12}
                        />
                    ) : null}
                </div>
            </Label>
            <Autosuggest
                color={color}
                id="template-user-suggest"
                value={query}
                shown={shown}
                setShown={setShown}
                onClick={onClick}
                onChange={onChangeQuery}
                disabled={disabled}
                placeholder={placeholder}
                onItemSelect={onItemSelect}
                onClear={clearSuggest}>
                <TemplateUserList id="template-user-suggest-list" busy={busy} response={response}/>
            </Autosuggest>
            <div style={{marginTop: 4}}>
                <CheckBox
                    id="include-inactive-users-checkbox"
                    checked={!onlyActive}
                    disabled={disabled}
                    label="Include inactive template users"
                    onCheckBoxChange={onToggleActive}
                />
            </div>
        </div>
    );
};
